import { lazy, Suspense, useCallback, useState } from 'react';
import { FormattedMessage } from 'react-intl';

import { Dialog, DialogContent, DialogTitle } from '@heidi/design-system/dialog';
import { ModalId } from '@heidi/schema/ml-scribe';
import { useAtom } from 'jotai';

import { evidencePostUpgradeOnboardingModalOpenAtom } from '../evidencePostUpgradeOnboardingAtoms';
import { getCommerceTrialsUiPorts } from '../trials-ui-ports';
import { EvidencePostUpgradeOnboardingModalLoading } from './evidence-post-upgrade-onboarding-modal-loading';
import { evidencePostUpgradeOnboardingMessages } from './messages';

const EvidencePostUpgradeOnboardingModalContent = lazy(() =>
  import('./evidence-post-upgrade-onboarding-modal-content').then((mod) => ({
    default: mod.EvidencePostUpgradeOnboardingModalContent,
  }))
);

const WELCOME_MODAL_ID = ModalId.EVIDENCE_POST_UPGRADE_WELCOME_POPUP;

/**
 * Three-step onboarding dialog for Evidence after upgrade, opened from the
 * post-upgrade welcome CTA via {@link evidencePostUpgradeOnboardingModalOpenAtom}.
 * On dismiss, removes the welcome popup from the user-triggered modal queue.
 * When the user opened this flow from "See what's new", {@link evidencePostUpgradeWelcomeDeferQueueUntilAfterOnboardingAtom}
 * is cleared in {@link EvidencePostUpgradeWelcomePopup} after seen/backend queue completion.
 */
export function EvidencePostUpgradeOnboardingModal() {
  const [isOpen, setIsOpen] = useAtom(evidencePostUpgradeOnboardingModalOpenAtom);
  const dequeueModal = getCommerceTrialsUiPorts().useDequeueModal();
  const [isDialogVisible, setIsDialogVisible] = useState(false);
  const Head = getCommerceTrialsUiPorts().Head;

  const handleDismiss = useCallback(() => {
    setIsOpen(false);
    dequeueModal(WELCOME_MODAL_ID);
  }, [dequeueModal, setIsOpen]);

  const handleOpenChange = useCallback(
    (open: boolean) => {
      if (!open) {
        handleDismiss();
      }
    },
    [handleDismiss]
  );

  return (
    <>
      {isOpen && (
        <Head>
          {/* Kick off the walkthrough fetch the moment the modal is opened, so the
              <video> element in step 1 is already buffered by the time the dialog
              finishes its open animation.

              Caveat: Safari/iOS silently ignore `rel="preload" as="video"`. The
              HTML5 `<video preload="auto">` attribute on the element itself acts
              as the universal fallback — Safari starts the fetch on mount, only
              losing the ~200 ms of head start Chrome/Edge/Firefox get from this
              hint. `as="fetch" crossorigin` is not a valid substitute (it would
              not populate the <video> resource cache and adds an unrelated CORS
              constraint). */}
          <link
            rel="preload"
            as="video"
            type="video/mp4"
            href="/videos/evidence/walkthrough.mp4"
          />
        </Head>
      )}
      <Dialog
        open={isOpen}
        onOpenChange={handleOpenChange}
        onOpenChangeComplete={setIsDialogVisible}
      >
        <DialogContent
          dismissible
          className="max-h-[min(90dvh,860px)] w-[min(calc(100vw-2rem),470px)] max-w-[470px] gap-0 overflow-y-auto border-border bg-surface p-8 shadow-sheet sm:rounded-xl"
          data-testid="evidence-post-upgrade-onboarding-modal"
        >
          <DialogTitle className="sr-only">
            <FormattedMessage
              {...evidencePostUpgradeOnboardingMessages.screenReaderTitle}
            />
          </DialogTitle>
          {isDialogVisible ? (
            <Suspense fallback={<EvidencePostUpgradeOnboardingModalLoading />}>
              <EvidencePostUpgradeOnboardingModalContent onRequestClose={handleDismiss} />
            </Suspense>
          ) : (
            <EvidencePostUpgradeOnboardingModalLoading />
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
